/* eslint-disable prefer-destructuring */
import { OptionCdts, OralHealthReportSubmit } from '@brands/Dashboard/ConsultationPage/assets/types';
import { CaseType, ICase, Status } from '@brands/services/cases/types/ICase';
import { ICDT } from '@brands/services/oneReport/types/IOneReport';
import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import React, { SetStateAction, useEffect, useMemo, useState } from 'react';
import { FieldErrors, UseFormGetValues, UseFormRegister, UseFormSetValue, UseFormTrigger } from 'react-hook-form';

import { listCDTOptions } from '../../../../services/identity/listCDTOptions';
import { UserProfile, UserRoleName } from '../../../../services/identity/types/UserProfile';
import { updateOneReport } from '../../../../services/oneReport/updateOneReport';
import { displayErrorDetails } from '../../../../Utils/displayError';
import { checkCollapsibleHeaderColor } from '../../../../Utils/getCollapsibleHeader';
import { evaluationCodes } from '../../assets/validationSchema';
import styles from '../../styles.module.scss';
import ServicesProvidedModal from './modal/servicesProvidedModal';
import ConfirmActionModal from '@brands/Dashboard/ConsultationPage/Modals/ConfirmActionModal/ConfirmActionModal';

type ServicesProvidedSectionProps = {
  consultationForm: OralHealthReportSubmit;
  setConsultationForm: React.Dispatch<React.SetStateAction<OralHealthReportSubmit>>;
  thisCase: ICase;
  setCase: React.Dispatch<SetStateAction<ICase>>;
  userInfo: UserProfile;
  register: UseFormRegister<OralHealthReportSubmit>;
  setValue: UseFormSetValue<OralHealthReportSubmit>;
  getValues: UseFormGetValues<OralHealthReportSubmit>;
  trigger: UseFormTrigger<OralHealthReportSubmit>;
  errors: FieldErrors<OralHealthReportSubmit>;
};

const ServicesProvidedSection = ({
  consultationForm,
  setConsultationForm,
  thisCase,
  setCase,
  userInfo,
  register,
  setValue,
  getValues,
  trigger,
  errors,
}: ServicesProvidedSectionProps): JSX.Element => {
  const [cdtOptions, setCdtOptions] = useState<OptionCdts[]>([]);
  const [originalCDTCodes, setOriginalCDTCodes] = useState<ICDT[]>([]);
  const [selectedEvaluationCode, setSelectedEvaluationCode] = useState<string | null>(null);
  const [otherCodes, setOtherCodes] = useState<string[]>([]);
  const [isCdtModalOpen, setIsCdtModalOpen] = useState(false);

  const [isDeleteCodeModalOpen, setIsDeleteCodeModalOpen] = useState(false);
  const [isDeletingCode, setIsDeletingCode] = useState(false);
  const [pendingRemoveCode, setPendingRemoveCode] = useState<string | null>(null);

  const cdtError = typeof errors.cdt?.message === 'string' ? errors.cdt.message : '';
  const hasCdtError = Boolean(cdtError);

  const saveCaseCDT = async (caseCDT: string[]): Promise<void> => {
    const { cdts: res } = await updateOneReport(
      thisCase?.one_report.id || '',
      {
        cdts: caseCDT,
      },
      userInfo.role.name,
    );

    setConsultationForm((prev) => ({
      ...prev,
      cdt: res.map((currentCDT) => currentCDT.code),
    }));

    setCase({
      ...thisCase,
      one_report: {
        ...thisCase.one_report,
        cdts: res,
      },
    });
  };

  const pendingCodeDetails = useMemo(
    () => originalCDTCodes.find((code) => code.code === pendingRemoveCode),
    [originalCDTCodes, pendingRemoveCode],
  );

  useEffect(() => {
    const currentCdtCodes = consultationForm.cdt;
    const initialCode = evaluationCodes.find((code) => currentCdtCodes.includes(code));

    if (initialCode) {
      setSelectedEvaluationCode(initialCode);
    } else {
      setSelectedEvaluationCode(null);
    }

    const uniqueCdtCodes = Array.from(new Set(currentCdtCodes));
    const filteredOtherCodes = uniqueCdtCodes.filter(
      (code) => code !== 'D9995' && code !== 'D9996' && !evaluationCodes.includes(code),
    );

    setOtherCodes(filteredOtherCodes);
  }, [consultationForm.cdt, thisCase.one_report.cdts]);

  const openRemoveCodeModal = (code: string): void => {
    setPendingRemoveCode(code);
    setIsDeleteCodeModalOpen(true);
  };

  const closeRemoveCodeModal = (): void => {
    setIsDeleteCodeModalOpen(false);
    setPendingRemoveCode(null);
  };

  const removeCode = async (codeToRemove: string): Promise<void> => {
    const updatedCdtCodes = consultationForm.cdt.filter((code) => code !== codeToRemove);

    if (evaluationCodes.includes(codeToRemove)) {
      setSelectedEvaluationCode(null);
    }

    setValue('cdt', updatedCdtCodes, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });

    setConsultationForm((prev) => ({
      ...prev,
      cdt: updatedCdtCodes,
    }));

    setOtherCodes(
      updatedCdtCodes.filter((code) => !evaluationCodes.includes(code) && code !== 'D9995' && code !== 'D9996'),
    );

    await saveCaseCDT(updatedCdtCodes);
    await trigger('cdt');
  };

  const confirmRemoveCode = async (): Promise<void> => {
    if (!pendingRemoveCode) return;

    try {
      setIsDeletingCode(true);
      await removeCode(pendingRemoveCode);
      closeRemoveCodeModal();
    } catch (error) {
      displayErrorDetails(error);
    } finally {
      setIsDeletingCode(false);
    }
  };

  useEffect(() => {
    const fetchCDTOptionsOnce = async (): Promise<void> => {
      try {
        const data = await listCDTOptions(userInfo.role.name);
        setOriginalCDTCodes(data);

        const transformedData = data
          .sort((a, b) => a.code.localeCompare(b.code))
          .map((cdt) => ({
            value: cdt.code,
            label: `${cdt.code}: ${cdt.nomenclature}`,
            disabled: cdt.code === 'D9995' || cdt.code === 'D9996',
            type: cdt.type,
          }));

        setCdtOptions(transformedData);
      } catch (error) {
        displayErrorDetails(error);
      }
    };

    fetchCDTOptionsOnce();
  }, [userInfo.role.name]);

  return (
    <div className={styles.assessmentDiv}>
      <div className={styles.cDT}>
        <div className={`${styles.cDTDiv} fs-unmask`}>
          <span>
            Services provided during visit <span className={`${styles.cDTDivSpanRequired} fs-unmask`}>*</span>
          </span>
        </div>
      </div>

      <div className={`${styles.cdtContainer} ${hasCdtError ? styles.cdtContainerError : ''}`}>
        <div className={`${styles.fixedValue} ${styles.cdtChildren}`}>
          <div className={`fs-exclude ${styles.boldText}`}>
            {thisCase?.type === 'video_call_scheduled' ||
            thisCase?.type === 'video_call_instant' ||
            thisCase?.type === CaseType.phone_call_scheduled
              ? 'D9995: Teledentistry'
              : 'D9996: Teledentistry'}
            <span className={styles.noteText}>Remove</span>
          </div>
          <div className="fs-exclude">
            {thisCase?.type === 'video_call_scheduled' ||
            thisCase?.type === 'video_call_instant' ||
            thisCase?.type === CaseType.phone_call_scheduled
              ? 'synchronous; real-time encounter'
              : 'Asynchronous; information stored and forwarded to dentist for subsequent review'}
          </div>
        </div>

        {selectedEvaluationCode && (
          <div className={`${styles.evaluationCodesDiv} ${styles.cdtChildren}`}>
            <div className={styles.evaluationCodes}>
              <div className={styles.evaluationCodesInfo}>
                <div className={`fs-exclude ${styles.boldText}`}>
                  {originalCDTCodes.find((item) => item.code === selectedEvaluationCode)?.code}
                  {userInfo.role.name === UserRoleName.Provider && thisCase.status === Status.InProgressProvider && (
                    <span
                      className={`fs-unmask ${styles.trashIcon}`}
                      onClick={() => openRemoveCodeModal(selectedEvaluationCode)}
                      title="Remove this evaluation code"
                    >
                      Remove
                    </span>
                  )}
                </div>
                <div className="fs-exclude">
                  {originalCDTCodes.find((item) => item.code === selectedEvaluationCode)?.nomenclature}
                </div>
              </div>
            </div>
          </div>
        )}

        {otherCodes.length > 0 &&
          otherCodes.map((otherCode) => (
            <div key={otherCode} className={`${styles.otherCodesDiv} ${styles.cdtChildren}`}>
              <div className={styles.otherCodes}>
                <div className={styles.evaluationCodesInfo}>
                  <div className={`fs-exclude ${styles.boldText}`}>
                    {originalCDTCodes.find((code) => code.code === otherCode)?.code}
                    {userInfo.role.name === UserRoleName.Provider && thisCase.status === Status.InProgressProvider && (
                      <span
                        className={`fs-unmask ${styles.trashIcon}`}
                        onClick={() => openRemoveCodeModal(otherCode)}
                        title="Remove this other code"
                      >
                        Remove
                      </span>
                    )}
                  </div>
                  <div className="fs-exclude">
                    {originalCDTCodes.find((code) => code.code === otherCode)?.nomenclature}
                  </div>
                </div>
              </div>
            </div>
          ))}
      </div>

      {(checkCollapsibleHeaderColor(thisCase || '', userInfo, 'Assessment') === 'edit' ||
        checkCollapsibleHeaderColor(thisCase || '', userInfo, 'Assessment') === 'editNotRequired') && (
        <Button
          className={`${styles.addCdtCodeModal} ${hasCdtError ? styles.addCdtCodeModalError : ''}`}
          onClick={() => setIsCdtModalOpen(true)}
        >
          <AddIcon />
          Add CDT evaluation code (required)
        </Button>
      )}

      {hasCdtError && <div className={styles.sectionErrorText}>{cdtError}</div>}

      <ServicesProvidedModal
        open={isCdtModalOpen}
        onClose={() => setIsCdtModalOpen(false)}
        consultationForm={consultationForm}
        setConsultationForm={setConsultationForm}
        thisCase={thisCase}
        setCase={setCase}
        originalCDTCodes={originalCDTCodes}
        cdtOptions={cdtOptions}
        setCdtOptions={setCdtOptions}
        saveCaseCDT={saveCaseCDT}
        evaluationCodes={evaluationCodes}
      />

      <ConfirmActionModal
        open={isDeleteCodeModalOpen}
        title="Remove CDT code"
        message={`Are you sure you want to remove ${
          pendingCodeDetails?.code
            ? `${pendingCodeDetails.code}${pendingCodeDetails.nomenclature ? `: ${pendingCodeDetails.nomenclature}` : ''}`
            : 'this CDT code'
        }?`}
        confirmText="Remove"
        cancelText="Cancel"
        destructive
        isLoading={isDeletingCode}
        onClose={closeRemoveCodeModal}
        onConfirm={confirmRemoveCode}
      />
    </div>
  );
};

export default React.memo(ServicesProvidedSection);